import React, { useEffect, useMemo, useState } from 'react';
import ringArt from '../../../assets/img/portal-ring.png';

/**
 * The 2.1 seconds after SEND.
 * Handoff: docs/plans/2026-09-portals-stories/handoff-send/README.md.
 *
 * The upload starts at t=0, underneath this — starting the animation and starting the upload
 * are the same action, and the animation never gates the bytes. It always plays its full
 * length, even when a text Portal finishes in under a second, and even when the upload fails:
 * Featured is where a failure gets reported, not here. It can't be skipped, because it's short.
 *
 * The ring spins on an inner element while the pop scales an outer one, so the two transforms
 * don't fight over the same node.
 */
const RING_BOX = 208;
const EASE_OUT = 'cubic-bezier(0,0,0.2,1)';
const EASE_IN = 'cubic-bezier(0.4,0,1,1)';

export const TAKEOVER_MS = 2100;
const EXIT_AT = 1750;
const TITLE_SWAP_AT = 1250;
const REDUCED_HOLD_MS = 600;
const REDUCED_TOTAL_MS = 800;

function prefersReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

export default function SendingTakeover({ coverUrl = null, avatarUrl = null, roomLabel = 'FRIENDS', onDone }) {
  const reduced = useMemo(prefersReducedMotion, []);
  const [title, setTitle] = useState('Opening your Portal');
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timers = [];
    if (reduced) {
      // No pop, no spin, no ripples: the end state, a beat, then Featured.
      timers.push(setTimeout(() => setExiting(true), REDUCED_HOLD_MS));
      timers.push(setTimeout(() => onDone?.(), REDUCED_TOTAL_MS));
    } else {
      timers.push(setTimeout(() => setTitle('On its way'), TITLE_SWAP_AT));
      timers.push(setTimeout(() => setExiting(true), EXIT_AT));
      timers.push(setTimeout(() => onDone?.(), TAKEOVER_MS));
    }
    return () => timers.forEach(clearTimeout);
  }, [reduced, onDone]);

  const anim = (name, ms, easing, delay = 0, extra = '') => (reduced
    ? undefined
    : `${name} ${ms}ms ${easing} ${delay}ms ${extra}`.trim());

  return (
    <div
      className="absolute inset-0 z-[20] overflow-hidden"
      style={{
        background: '#000',
        animation: exiting
          ? `prFadeOut 320ms ${EASE_IN} forwards`
          : anim('prFadeIn', reduced ? 0 : 220, 'linear'),
      }}
      aria-live="polite"
      role="status"
    >
      {/* The cover, barely there and drifting. A text Portal has none, so this stays black. */}
      {coverUrl && (
        <div
          className="pr-takeover-animated absolute"
          style={{
            inset: '-10%',
            backgroundImage: `url(${coverUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.35,
            transform: 'scale(1.08)',
            animation: anim('prDrift', 2200, 'linear', 0, 'forwards'),
          }}
          aria-hidden
        />
      )}
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.55)' }} aria-hidden />

      <div
        className="absolute inset-0 flex flex-col items-center justify-center"
        style={{ animation: exiting ? `prSink 320ms ${EASE_IN} forwards` : undefined }}
      >
        <div className="relative" style={{ width: RING_BOX, height: RING_BOX }}>
          {/* One ring leaves every 500ms. Outlines only — cyan is never a fill. */}
          {!reduced && [350, 850, 1350].map((delay) => (
            <span
              key={delay}
              className="absolute inset-0 rounded-full"
              style={{
                border: '2px solid #00FFFF',
                opacity: 0,
                animation: `prRipple 1500ms ${EASE_OUT} ${delay}ms infinite`,
              }}
              aria-hidden
            />
          ))}

          {/* Pop on the wrapper, spin on the child. */}
          <span
            className="pr-takeover-animated absolute inset-0"
            style={{ animation: anim('prPop', 420, EASE_OUT, 0, 'both') }}
            aria-hidden
          >
            <span
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${ringArt})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                animation: reduced ? undefined : 'prSpin 1100ms linear infinite',
              }}
            />
          </span>

          <span
            className="pr-takeover-animated absolute overflow-hidden rounded-full"
            style={{ inset: '5.5%', background: '#1A1A1A', animation: anim('prPop', 420, EASE_OUT, 120, 'both') }}
          >
            {avatarUrl && <img src={avatarUrl} alt="" className="h-full w-full object-cover" />}
          </span>
        </div>

        <p
          key={title}
          className="pr-takeover-animated text-center font-mono font-bold uppercase text-white"
          style={{
            marginTop: 40,
            fontSize: 14,
            letterSpacing: '0.10em',
            animation: anim('prRise', 320, EASE_OUT, title === 'On its way' ? 0 : 300, 'both'),
          }}
        >
          {title}
        </p>

        <p
          className="pr-takeover-animated text-center font-mono font-bold uppercase"
          style={{
            marginTop: 10,
            fontSize: 10,
            letterSpacing: '0.10em',
            color: 'rgba(255,255,255,0.65)',
            animation: anim('prRise', 320, EASE_OUT, 360, 'both'),
          }}
        >
          {roomLabel}
        </p>
      </div>
    </div>
  );
}
